import React from 'react';
import type { SetURLSearchParams } from 'react-router-dom';
import type { SearchResponse } from '../domain';
import { decodeHtml, buildPropertyUrl } from '../../../app/utils';
import { MapView } from './MapView';

interface PropertyListingProps {
  data: SearchResponse | null;
  loading: boolean;
  error: string | null;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

function SkeletonCard() {
  return (
    <div
      className="overflow-hidden animate-pulse"
      style={{ background: 'var(--surface-lowest)', borderRadius: '1rem' }}
    >
      <div style={{ height: '16rem', background: 'var(--surface-container)' }} />
      <div className="p-6 space-y-3">
        <div style={{ height: '1rem', background: 'var(--surface-container)', borderRadius: '0.25rem', width: '75%' }} />
        <div style={{ height: '0.75rem', background: 'var(--surface-container)', borderRadius: '0.25rem', width: '50%' }} />
        <div className="mt-4" style={{ height: '1.5rem', background: 'var(--surface-container)', borderRadius: '0.25rem', width: '33%' }} />
      </div>
    </div>
  );
}

export function PropertyListing({
  data,
  loading,
  error,
  searchParams,
  setSearchParams,
}: PropertyListingProps) {
  const isMapView = searchParams.get('mp') === 'true';

  function toggleView(mode: 'list' | 'map') {
    const next = new URLSearchParams(searchParams);
    next.set('mp', mode === 'map' ? 'true' : 'false');
    setSearchParams(next);
  }

  if (error) {
    return (
      <div
        className="p-4 text-sm"
        role="alert"
        style={{
          background: 'var(--surface-high)',
          color: 'var(--color-error)',
          borderRadius: '0.75rem',
        }}
      >
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 face_layout__main_content">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="lift p-10 face_layout__main_content w-full text-center"
        style={{ background: 'var(--surface-lowest)', borderRadius: '1rem' }}
      >
        <div className="eyebrow mb-3">Ready when you are</div>
        <p className="text-base" style={{ color: 'var(--on-surface-variant)' }}>
          Enter a destination and dates to search for properties.
        </p>
      </div>
    );
  }

  return (
    <div className="face_layout__main_content w-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 gap-4">
        <div>
          <div className="eyebrow mb-2">Results</div>
          <h2
            className="font-display text-3xl md:text-4xl font-light leading-tight"
            style={{ color: 'var(--on-surface)' }}
          >
            {data.searchResultTotalLabel}
          </h2>
          {data.searchSummaryLabel && (
            <p className="text-sm mt-2" style={{ color: 'var(--on-surface-variant)' }}>
              {data.searchSummaryLabel}
            </p>
          )}
        </div>
        {data.hasResults && (
          <div
            className="flex items-center p-1"
            style={{ background: 'var(--surface-high)', borderRadius: '9999px' }}
          >
            <ViewToggleButton active={!isMapView} onClick={() => toggleView('list')} label="List">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </ViewToggleButton>
            <ViewToggleButton active={isMapView} onClick={() => toggleView('map')} label="Map">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </ViewToggleButton>
          </div>
        )}
      </div>

      {isMapView && data.hasResults && (
        <MapView
          properties={data.properties}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      )}

      {!isMapView && data.hasResults && data.properties.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {data.properties.map((property) => (
            <a
              key={property.identifier}
              href={buildPropertyUrl(property.identifier, searchParams)}
              className="group block overflow-hidden transition-transform duration-300"
              style={{ background: 'var(--surface-lowest)', borderRadius: '1rem' }}
            >
              <div
                className="relative overflow-hidden"
                style={{ height: '16rem', background: 'var(--surface-container)' }}
              >
                {property.coverImage ? (
                  <img
                    src={property.coverImage}
                    alt={property.titleLabel}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      style={{ color: 'var(--on-surface-variant)', opacity: 0.4 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3
                  className="font-display text-xl font-normal leading-snug mb-2 line-clamp-2"
                  style={{ color: 'var(--on-surface)' }}
                >
                  {property.titleLabel}
                </h3>
                <p className="text-sm mb-6" style={{ color: 'var(--on-surface-variant)' }}>
                  {property.houseTypeDescLabel} · {property.roomLabel} · {property.occupancyLabel}
                </p>
                <div className="flex justify-between items-end">
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span
                        className="font-display text-2xl font-light"
                        style={{ color: 'var(--on-surface)' }}
                      >
                        {decodeHtml(property.perNightRateLabel)}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--on-surface-variant)' }}>
                        / night
                      </span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--on-surface-variant)' }}>
                      {decodeHtml(property.totalPriceLabel)} total
                    </p>
                  </div>
                  <span className="tertiary text-sm">
                    View
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      {!data.hasResults && (
        <div
          className="lift p-10 md:p-14"
          style={{ background: 'var(--surface-lowest)', borderRadius: '1rem' }}
        >
          <div className="max-w-xl">
            <div className="eyebrow mb-3">No matches</div>
            <h3
              className="font-display text-3xl md:text-4xl font-light leading-tight mb-4"
              style={{ color: 'var(--on-surface)' }}
            >
              Nothing fits just yet.
            </h3>
            <p className="text-base mb-10" style={{ color: 'var(--on-surface-variant)' }}>
              We couldn't find any listings for "{data.placeName}" with your selected criteria. Try adjusting your dates or explore a neighbouring destination.
            </p>
          </div>
          {data.suggestedPlaces && data.suggestedPlaces.length > 0 && (
            <div>
              <div className="eyebrow mb-5">Try these destinations</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.suggestedPlaces.map((place) => {
                  const sp = new URLSearchParams(searchParams);
                  sp.set('destination', place.name);
                  sp.set('destinationId', place.identifier);
                  return (
                    <a
                      key={place.identifier}
                      href={`/search?${sp.toString()}`}
                      className="block p-5 transition-colors"
                      style={{
                        background: 'var(--surface-low)',
                        borderRadius: '0.75rem',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-container)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface-low)')}
                    >
                      <div
                        className="font-display text-lg font-normal mb-1"
                        style={{ color: 'var(--on-surface)' }}
                      >
                        {place.name}
                      </div>
                      {place.description && (
                        <div
                          className="text-sm line-clamp-2"
                          style={{ color: 'var(--on-surface-variant)' }}
                        >
                          {place.description}
                        </div>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ViewToggleButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}

function ViewToggleButton({ active, onClick, label, children }: ViewToggleButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors"
      style={{
        background: active ? 'var(--surface-lowest)' : 'transparent',
        color: active ? 'var(--on-surface)' : 'var(--on-surface-variant)',
        borderRadius: '9999px',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        {children}
      </svg>
      {label}
    </button>
  );
}
